import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  getIgnoreMaintenanceMode,
  getMaintenanceModeSecretToken,
} from "./bridges/maintenanceBridge";

/**
 * Shared Axios instance for API calls.
 *
 * IMPORTANT:
 * This client must be tolerant during application startup.
 * It must NEVER hard-fail requests just because authentication
 * is not ready yet.
 */
const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_CALLBACK_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor
 *
 * Responsibilities:
 * 1. Resolve correct base URL depending on environment
 * 2. Attach auth token if available (best-effort)
 * 3. Attach maintenance bypass header if active
 *
 * CRITICAL DESIGN RULE:
 * ❌ Never throw inside a request interceptor because of missing auth.
 * Auth errors must be handled via HTTP responses (401/403).
 */
axiosClient.interceptors.request.use(
  async (config) => {
    /* -------------------------------------------------------------
     * 1) Base URL resolution
     * ----------------------------------------------------------- */
    if (import.meta.env.VITE_ENV === "development") {
      config.baseURL = import.meta.env.VITE_FARMER_API_URL;
    }

    /* -------------------------------------------------------------
     * 2) Attach Authorization token (best effort)
     * ----------------------------------------------------------- */
    try {
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken?.toString();

      if (jwt) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${jwt}`;
      }
    } catch {
      // No session yet -> proceed without Authorization header.
    }

    /* -------------------------------------------------------------
     * 3) Maintenance bypass header (optional)
     * ----------------------------------------------------------- */
    config.headers = config.headers ?? {};

    if (getIgnoreMaintenanceMode()) {
      const token = getMaintenanceModeSecretToken();
      if (token) {
        config.headers["X-Bypass-Maintenance"] = token;
      }
    } else {
      delete config.headers["X-Bypass-Maintenance"];
    }

    return config;
  },
  (error) => {
    console.error("[HTTP] Request setup failed:", error);
    return Promise.reject(error);
  }
);

export default axiosClient;
